import React from 'react';
import './PurchaseOrderReceipt.css';

/**
 * PurchaseOrderReceipt Component
 * Located in CommonComponents/Invoice Receipts/
 * 
 * Reusable, printable purchase order invoice receipt matching reference specification:
 * - Hospital header: VISWATEJA HOSPITALS & Purchase Order GRN
 * - Two side-by-side cards: Supplier info & Order info
 * - Total Items indicator above table
 * - 11-column Items table with solid borders: Item | Batch | Expiry | PackQty (units) | Quantity | Free | Mrp | Rate | Disc | Gst | Amount
 * - Bottom two-column layout: GST Breakdown table on left, Order Summary box on right
 */
const PurchaseOrderReceipt = React.forwardRef(({
  order = {},
  supplier = {},
  items = [],
  discountAmount = 0,
  roundoffAmount = 0,
  effectiveRoundoff = 0,
  taxableSubTotal = 0,
  grossBaseTotal = 0,
  totalSGST = 0,
  totalCGST = 0,
  gstBreakdown = [],
  billDiscountPct = 0,
  grandTotal = 0,
  calculateItemAmount,
  formatDateHuman
}, ref) => {
  // Format Date Helper
  const defaultFormatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const formatDate = formatDateHuman || defaultFormatDate;

  // Supplier fields resolution
  const activeSupplierName = order.supplierName || supplier?.name || '—';
  const activeGstNo = supplier?.gstNumber || order.supplierGst || '—';
  const activeDlNo1 = supplier?.dl1Number || supplier?.dlNumber1 || order.supplierDl1 || '—';
  const activeDlNo2 = supplier?.dl2Number || supplier?.dlNumber2 || order.supplierDl2 || '—';
  const activePhone = supplier?.phone1 || supplier?.phone || supplier?.mobile || order.supplierPhone || '—';

  // Order meta fields resolution
  const activeCreatedBy = order.representativeName || order.createdBy || 'Admin';
  const activeCreatedOn = formatDate(order.createdDate || order.createdAt || new Date());
  const activeInvoiceNo = order.invoiceNumber || order.grnNo || '—';
  const activeInvoiceDate = formatDate(order.invoiceDate);

  // Round off value to display
  const activeRoundOff = effectiveRoundoff !== undefined ? effectiveRoundoff : roundoffAmount;

  // Fallback item amount calculation if calculateItemAmount is not passed
  const getItemCalc = (it) => {
    if (calculateItemAmount) return calculateItemAmount(it);
    const qty = Number(it.quantity) || 0;
    const packQty = Math.max(1, Number(it.packQuantity) || 1);
    const packRate = Number(it.packRate != null ? it.packRate : it.rate) || 0;
    const base = qty * packRate;
    let disc = 0;
    if (it.discountPercent) {
      disc = (base * Number(it.discountPercent)) / 100;
    } else if (it.discountAmount) {
      disc = Number(it.discountAmount);
    }
    const taxable = Math.max(0, base - disc);
    return {
      discountAmount: disc,
      taxableAmount: taxable
    };
  };

  return (
    <div className="vt-po-print-document" ref={ref}>
      {/* Document Header in sync with other hospital reports */}
      <div className="receipt-header print-receipt-header">
        <img src="/VThospitalsLogo.png" alt="Hospital Logo" className="logo print-hospital-logo" />
        <h2 className="clinic-name print-clinic-name">VISWATEJA HOSPITAL &amp; DIAGNOSTIC CENTRE</h2>
        <p className="clinic-address print-clinic-address">26-4-788/1, RPGT Road, Near Srinivasa Talkies, Hindupur - 515201</p>
      </div>

      {/* PO Title & GRN Bar */}
      <div className="print-po-title-bar">
        <span className="print-po-title-text">PURCHASE ORDER</span>
        <span className="print-po-grn-tag">GRN: {order.grnNo || order.invoiceNumber || 'PO-2026'}</span>
      </div>

      {/* Two Side-by-Side Information Cards */}
      <div className="print-info-grid">
        {/* Left Box: Supplier Details */}
        <div className="print-info-box">
          <div className="print-info-row">
            <span className="print-info-lbl">Supplier Name</span>
            <span className="print-info-val"><strong>{activeSupplierName}</strong></span>
          </div>
          <div className="print-info-row">
            <span className="print-info-lbl">GST No</span>
            <span className="print-info-val">{activeGstNo}</span>
          </div>
          <div className="print-info-row">
            <span className="print-info-lbl">DL No1</span>
            <span className="print-info-val">{activeDlNo1}</span>
          </div>
          <div className="print-info-row">
            <span className="print-info-lbl">DL No2</span>
            <span className="print-info-val">{activeDlNo2}</span>
          </div>
          <div className="print-info-row">
            <span className="print-info-lbl">Phone</span>
            <span className="print-info-val">{activePhone}</span>
          </div>
        </div>

        {/* Right Box: Order / Invoice Details */}
        <div className="print-info-box">
          <div className="print-info-row">
            <span className="print-info-lbl">Created By</span>
            <span className="print-info-val">{activeCreatedBy}</span>
          </div>
          <div className="print-info-row">
            <span className="print-info-lbl">Created On</span>
            <span className="print-info-val">{activeCreatedOn}</span>
          </div>
          {(order.status === 'PROCESSED_AND_RESTOCKED' || order.status === 'PROCESSED' || order.status === 'RESTOCKED' || order.status === 'PROCESSED AND RESTOCKED') && (
            <>
              <div className="print-info-row">
                <span className="print-info-lbl">Processed By</span>
                <span className="print-info-val"><strong>{order.processedBy || activeCreatedBy}</strong></span>
              </div>
              <div className="print-info-row">
                <span className="print-info-lbl">Processed On</span>
                <span className="print-info-val">{formatDate(order.processedAt || order.updatedAt)}</span>
              </div>
            </>
          )}
          {order.status === 'DELETED' && (
            <>
              <div className="print-info-row">
                <span className="print-info-lbl">Deleted By</span>
                <span className="print-info-val"><strong>{order.deletedBy || activeCreatedBy}</strong></span>
              </div>
              <div className="print-info-row">
                <span className="print-info-lbl">Deleted On</span>
                <span className="print-info-val">{formatDate(order.deletedAt || order.updatedAt)}</span>
              </div>
            </>
          )}
          <div className="print-info-row">
            <span className="print-info-lbl">Invoice No</span>
            <span className="print-info-val"><strong>{activeInvoiceNo}</strong></span>
          </div>
          <div className="print-info-row">
            <span className="print-info-lbl">Invoice Date</span>
            <span className="print-info-val">{activeInvoiceDate}</span>
          </div>
        </div>
      </div>

      {/* Total Items Line Above Table */}
      <div className="print-total-items-line">
        Total Items: <strong>{items.length}</strong>
      </div>

      {/* 11-Column Items Table (Solid Borders) */}
      <table className="print-items-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left', width: '22%' }}>Item</th>
            <th style={{ textAlign: 'center', width: '9%' }}>Batch</th>
            <th style={{ textAlign: 'center', width: '9%' }}>Expiry</th>
            <th style={{ textAlign: 'center', width: '10%' }}>
              PackQty <span className="print-unit-sub">(units)</span>
            </th>
            <th style={{ textAlign: 'right', width: '8%' }}>Qty (Units)</th>
            <th style={{ textAlign: 'right', width: '7%' }}>Free (Units)</th>
            <th style={{ textAlign: 'right', width: '7%' }}>Mrp</th>
            <th style={{ textAlign: 'right', width: '7%' }}>Rate</th>
            <th style={{ textAlign: 'right', width: '6%' }}>Disc</th>
            <th style={{ textAlign: 'right', width: '6%' }}>Gst</th>
            <th style={{ textAlign: 'right', width: '9%' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={11} style={{ textAlign: 'center', padding: '14px', color: '#64748b' }}>
                No items in this purchase order.
              </td>
            </tr>
          ) : (
            items.map((it, idx) => {
              const calc = getItemCalc(it);
              const packQty = Math.max(1, Number(it.packQuantity) || 1);
              const quantityInUnits = Number(it.quantity || 0) * packQty;
              const freeInUnits = Number(it.freeQuantity || 0) * packQty;
              const expDisplay = it.expiryDate
                ? (it.expiryDate.includes('-')
                    ? it.expiryDate.split('-').slice(0, 2).reverse().join('/')
                    : it.expiryDate)
                : '—';
              return (
                <tr key={it.id || idx}>
                  <td style={{ textAlign: 'left', fontWeight: '600' }}>{it.brand || it.itemName}</td>
                  <td style={{ textAlign: 'center' }}>{it.batchNo || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{expDisplay}</td>
                  <td style={{ textAlign: 'center' }}>{packQty}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600' }}>{quantityInUnits}</td>
                  <td style={{ textAlign: 'right' }}>{freeInUnits}</td>
                  <td style={{ textAlign: 'right' }}>{Number(it.mrp || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(it.rate || it.packRate || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(calc.discountAmount || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{it.gstPercent || 0}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600' }}>
                    {Number(calc.taxableAmount || 0).toFixed(2)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Bottom Section: GST Breakdown & Summary Box */}
      <div className="print-bottom-grid">
        {/* Left: GST Breakdown Table */}
        <div className="print-gst-wrapper">
          <table className="print-gst-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Class</th>
                <th>Sub total</th>
                <th>Discount</th>
                <th>SGST</th>
                <th>CGST</th>
                <th>Total GST</th>
              </tr>
            </thead>
            <tbody>
              {gstBreakdown.map((row) => (
                <tr key={row.rate}>
                  <td style={{ textAlign: 'left', fontWeight: '700' }}>GST {row.rate}%</td>
                  <td>{(row.subtotal != null ? row.subtotal : row.taxable).toFixed(2)}</td>
                  <td>{(row.discount || 0).toFixed(2)}</td>
                  <td>{(row.sgst || 0).toFixed(2)}</td>
                  <td>{(row.cgst || 0).toFixed(2)}</td>
                  <td>{(row.total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Right: Summary Box */}
        <div className="print-summary-box">
          <div className="print-summary-row">
            <span className="print-summary-lbl">Sub total</span>
            <span className="print-summary-val">{Number(taxableSubTotal || grossBaseTotal).toFixed(2)}</span>
          </div>
          <div className="print-summary-row">
            <span className="print-summary-lbl">SGST payable</span>
            <span className="print-summary-val">{Number(totalSGST).toFixed(2)}</span>
          </div>
          <div className="print-summary-row">
            <span className="print-summary-lbl">CGST payable</span>
            <span className="print-summary-val">{Number(totalCGST).toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="print-summary-row">
              <span className="print-summary-lbl">
                Bill Discount {billDiscountPct > 0 ? `( ${billDiscountPct} % )` : ''}
              </span>
              <span className="print-summary-val">{Number(discountAmount).toFixed(2)}</span>
            </div>
          )}
          <div className="print-summary-row">
            <span className="print-summary-lbl">RoundOff</span>
            <span className="print-summary-val">
              {activeRoundOff > 0 ? `+${Number(activeRoundOff).toFixed(2)}` : Number(activeRoundOff || 0).toFixed(2)}
            </span>
          </div>
          <div className="print-summary-row grand-total">
            <span className="print-summary-lbl">Grand total</span>
            <span className="print-summary-val">{Number(grandTotal).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PurchaseOrderReceipt;
